import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { ProductGroupStatistics } from '../ProductGroupStatistics';
import SelectionBulkContainer from '../../../../shop/selectionlists/header/SelectionBulkContainer';
import SelectionBulkMenu from '../../../../shared/components/SelectionBulkMenu';
import {
    BUTTON_SIZE_LARGE,
    BUTTON_THEME_EMPTY_GREEN,
    BUTTON_THEME_FULL_GREEN
} from '../../../../shared/common/Button/themes';
import ConfirmationModalLayout from '../../../../shared/components/modal/ConfirmationModalLayout';
import {
    StBackButton,
    StButtonTextWidthAuto,
    StCheck,
    StContainer,
    StHeadlinePart,
    StMoreButton,
    StNameInput,
    StTitle,
    StTitleForm
} from './Headline.styled';
import { MATERIAL_ICON_EDIT, MATERIAL_ICON_SORT_BY } from '../../../../shared/common/Button/Icon';
import EditPropertiesModal from './EditPropertiesModal';
import { useRouteMatch } from 'react-router';
import { CAT_MAN_PRODUCT_GROUP_SORTING } from '../../../../shared/common/SortByDropDown';
import { Tooltip } from '../../../../shared/components/Tooltip';
import { useSaveLibraryManagedProductGroupSortBy } from '../api/useSaveLibraryManagedProductGroupSortBy';
import { Orderlist } from '../../../../../models/OrderList';
import {
    BulkAddProductsToSelectionListResponse,
    BulkAddProductsToShoppingCartResponse,
    UsageStatisticsOut
} from '../../../../store/api';
import { useTranslation } from 'react-i18next';
import { ButtonIconTextWidthAuto } from '../../../../shared/common/Button/ButtonIconTextWidthAuto';
import { LibraryManagedProductGroupOnSiteOut } from '../../../../store/api-catman';
import { getPaginationStateObject } from '../../../../shared/common/Pagination';
import { modifyURLStates } from '../../../../shared/utils/utils-routing';
import { SubscriptionPeriodType } from '../../../../../models/Article';
import { PriceTier } from '../../../../../models/PriceTier';
import { ProductSelection } from '../../../../shared/product-selection/ProductSelectionTypes';
import { getProductSelectionCount } from '../../../../shared/product-selection/ProductSelectionUtils';

interface Props {
    active: boolean;
    title: string;
    isInSelectionMode: boolean;
    isAddToListEnabled: boolean;
    onRemoveFromProductGroup: () => void;
    productSelection: ProductSelection;
    productGroups: ReadonlyArray<LibraryManagedProductGroupOnSiteOut>;
    cancelSelectionMode: () => void;
    usageStatistics: UsageStatisticsOut | undefined;
    onSaveTitle: (title: string) => void;
    onToggleActive: (active: boolean) => void;
    onRemoveAllProducts: () => void;
    onBulkAddToGroup: (productGroupId: number) => void;
    onBulkAddToCart: (
        performChecks: boolean,
        priceTier: PriceTier,
        subscriptionPeriodType: SubscriptionPeriodType
    ) => Promise<BulkAddProductsToShoppingCartResponse>;
    productAmount: number;
    sorting: string;
    onAddToList: (
        list: Orderlist,
        performChecks: boolean,
        priceTier: PriceTier,
        subscriptionPeriodType: SubscriptionPeriodType
    ) => Promise<BulkAddProductsToSelectionListResponse>;
    isNothingSelected: boolean;
}

const Headline: FC<Props> = ({
    title,
    active,
    isAddToListEnabled,
    onSaveTitle,
    usageStatistics = {
        ebooksAmountOnSite: 0,
        ebooksOnLoanOnSite: 0,
        ebooksLoanableOnSite: 0,
        eaudiobooksAmountOnSite: 0,
        eaudiobooksOnLoanOnSite: 0,
        eaudiobooksLoanableOnSite: 0,
        epressAmountOnSite: 0,
        epressOnLoanOnSite: 0,
        epressLoanableOnSite: 0
    },
    onBulkAddToGroup,
    cancelSelectionMode,
    productAmount,
    isInSelectionMode,
    onRemoveAllProducts,
    productSelection,
    onRemoveFromProductGroup,
    productGroups,
    onToggleActive,
    onBulkAddToCart,
    sorting,
    onAddToList,
    isNothingSelected
}) => {
    const { t } = useTranslation();
    const titleFormRef = useRef() as React.MutableRefObject<HTMLInputElement>;
    const spacerRef = useRef() as React.MutableRefObject<HTMLInputElement>;
    const titleRef = useRef() as React.MutableRefObject<HTMLInputElement>;
    const editOrderRef = useRef(null);
    const history = useHistory();
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [editTitle, setEditTitle] = useState(false);
    const [productTitle, setProductTitle] = useState<string>('');
    const [visible, setVisible] = useState(false);
    const [isTooltipVisible, setIsTooltipVisible] = useState(false);
    const [productGroupSorting, setProductGroupSorting] = useState<{
        value: string;
        label: string;
    }>({ label: '', value: '' });
    const {
        params: { productGroupId }
    } = useRouteMatch<{ productGroupId: string }>();

    function startTitleEdit() {
        if (!editTitle) {
            setEditTitle(true);
            setProductTitle(productTitle);
        }
        titleRef.current.focus();
    }

    function cancelTitleEdit() {
        setEditTitle(false);
        setProductTitle(title);

        titleRef.current.blur();
    }

    function updateTitle(value) {
        setProductTitle(value);
    }

    function triggerSaveTitle() {
        if (!productTitleValid) {
            return;
        }

        if (productTitle !== title) {
            onSaveTitle(productTitle);
        }
        setEditTitle(false);
        titleRef.current.blur();
    }

    function handleShowConfirmation(state) {
        setShowConfirmation(state);
    }

    const { mutate } = useSaveLibraryManagedProductGroupSortBy({
        productGroupId,
        title,
        onSuccess: sorting => {
            const pageQueryState = getPaginationStateObject();
            const sortByQueryState = {
                name: 'sortBy',
                value: sorting === 'custom' ? 'custom' : 'releaseDateNewToOld',
                encode: false
            };
            const search = modifyURLStates(history.location.search, [pageQueryState, sortByQueryState]);
            history.replace({ pathname: history.location.pathname, search });
        }
    });

    function handleSaveSortingOrder() {
        mutate(productGroupSorting.value);
        setVisible(false);
    }

    const sortingKey = useMemo(() => {
        const item = CAT_MAN_PRODUCT_GROUP_SORTING.find(item => item.value === sorting);
        return item
            ? {
                  key: item.value,
                  label: t(`components.catManProductGroupSorting.${item.value}`, { defaultValue: item.label })
              }
            : { key: '', label: '' };
    }, [sorting, t]);

    function handleCancel() {
        setVisible(false);
        setProductGroupSorting({
            value: sortingKey.key,
            label: sortingKey.label
        });
    }

    useEffect(() => {
        setProductTitle(title);
        setProductGroupSorting({
            value: sortingKey.key,
            label: sortingKey.label
        });
    }, [title, sorting, sortingKey]);

    const productTitleValid = productTitle?.length >= 1 && productTitle?.length <= 255;

    const moreButton = (
        <StMoreButton className="default-dd-nav expanded-dd list-actions fl-none centered" title="more actions...">
            <a data-toggle-dropdown className="bt-dd" data-displayname={'MoreActionsMenu'} aria-label="More Actions">
                <i className="material-icons">more_horiz</i>
            </a>
            <div data-close-dropdown className="default-dd">
                <span style={{ right: '80px' }} className="active-arrow" />
                <ul>
                    <li>
                        <a
                            style={{ width: '150px' }}
                            onClick={() => handleShowConfirmation(true)}
                            className="display-block"
                            data-displayname="RemoveAllProductsOption"
                        >
                            <i className="material-icons normal fl-left">delete</i>
                            {t('marketing.productGroups.removeAllProducts')}
                        </a>
                    </li>
                </ul>
            </div>
        </StMoreButton>
    );

    const bulkMenu = (
        <SelectionBulkMenu
            isInSelectionMode={isInSelectionMode}
            isAddToListEnabled={isAddToListEnabled}
            cancelSelection={cancelSelectionMode}
            selectedCount={getProductSelectionCount(productSelection)}
            onRemoveFrom={onRemoveFromProductGroup}
            onBulkAddToGroup={onBulkAddToGroup}
            onBulkAddToCart={onBulkAddToCart}
            productGroups={productGroups}
            deleteLabel={t('marketing.productGroups.removeFromProductGroup')}
            isDeleteFromEnabled={true}
            isAddToProductGroupEnabled
            onBulkAddToList={onAddToList}
            isNothingSelected={isNothingSelected}
        />
    );

    const header = (
        <StContainer>
            <ConfirmationModalLayout
                visible={showConfirmation}
                title={t('marketing.productGroups.removeAllProducts')}
                text={t('marketing.productGroups.confirmRemove')}
                onConfirm={() => {
                    onRemoveAllProducts();
                    handleShowConfirmation(false);
                }}
                onCancel={() => handleShowConfirmation(false)}
            />
            <EditPropertiesModal
                title={t('marketing.productGroups.editProperties')}
                visible={visible}
                handleCancel={handleCancel}
                sorting={productGroupSorting?.value}
                onSortChange={e => {
                    setProductGroupSorting(e);
                }}
                onSave={handleSaveSortingOrder}
            />
            <StHeadlinePart>
                <StBackButton onClick={() => window.history.back()}>
                    <i className="material-icons" style={{ fontSize: 'inherit' }}>
                        arrow_back
                    </i>
                </StBackButton>
                <StTitle>
                    <StTitleForm ref={titleFormRef} className="list-name-form" active={editTitle}>
                        <StNameInput
                            ref={titleRef}
                            value={productTitle}
                            minWidthInPx={50}
                            onClick={startTitleEdit}
                            onChange={updateTitle}
                            onEnterKey={triggerSaveTitle}
                            onEscKey={cancelTitleEdit}
                            truncated={!editTitle}
                            onFocus={startTitleEdit}
                            maxLength={80}
                        />
                        <StCheck
                            onClick={triggerSaveTitle}
                            className="material-icons"
                            active={editTitle && productTitleValid}
                        >
                            check
                        </StCheck>
                    </StTitleForm>
                </StTitle>
                {moreButton}
                <div ref={spacerRef} style={{ flexGrow: 1 }} />
            </StHeadlinePart>
            <StHeadlinePart>
                <ProductGroupStatistics usageStatistics={usageStatistics} />

                <ButtonIconTextWidthAuto
                    size={BUTTON_SIZE_LARGE}
                    onClick={() => setVisible(true)}
                    theme={BUTTON_THEME_EMPTY_GREEN}
                    icon={MATERIAL_ICON_EDIT}
                    className="ml-2"
                    dataDisplayname="EditPropertiesButton"
                >
                    {t('marketing.productGroups.editProperties')}
                </ButtonIconTextWidthAuto>
                <div
                    ref={editOrderRef}
                    onMouseOver={() => setIsTooltipVisible(true)}
                    onMouseOut={() => setIsTooltipVisible(false)}
                >
                    <ButtonIconTextWidthAuto
                        size={BUTTON_SIZE_LARGE}
                        onClick={() => history.push(`${history.location.pathname}/sorting`)}
                        disabled={sorting !== 'custom'}
                        theme={BUTTON_THEME_EMPTY_GREEN}
                        icon={MATERIAL_ICON_SORT_BY}
                        dataDisplayname="EditProductOrderButton"
                        className="ml-2"
                    >
                        {t('marketing.productGroups.editCustomProductOrder')}
                    </ButtonIconTextWidthAuto>
                </div>
                <Tooltip
                    size="large"
                    target={editOrderRef}
                    visible={productGroupSorting?.value !== 'custom' && isTooltipVisible}
                    placement="top"
                >
                    <p>{t('marketing.productGroups.editTooltip')}</p>
                </Tooltip>

                <StButtonTextWidthAuto
                    size={BUTTON_SIZE_LARGE}
                    theme={active ? BUTTON_THEME_EMPTY_GREEN : BUTTON_THEME_FULL_GREEN}
                    onClick={() => onToggleActive(!active)}
                    disabled={!active && productAmount === 0}
                    dataDisplayname={'ToggleActiveButton'}
                    className="ml-2"
                >
                    {active
                        ? t('marketing.productGroups.deactivateProductGroup')
                        : t('marketing.productGroups.activateProductGroup')}
                </StButtonTextWidthAuto>
            </StHeadlinePart>
        </StContainer>
    );
    return (
        <SelectionBulkContainer
            movedUp={isInSelectionMode}
            isSticky={false}
            headerComponent={header}
            bulkMenuComponent={bulkMenu}
        />
    );
};

export default Headline;
